/* eslint-disable react/no-unescaped-entities, no-empty */
import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PerchePASection, ComeFunzionaSection } from "@/pages/coc/CoCSections";

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password, remember);
      toast.success(`Bentornato ${u.nome}!`);
      const dest = u.ruolo === "admin" ? "/admin"
        : u.ruolo === "owner" ? "/proprietari/dashboard"
        : "/agent";
      nav(dest);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenziali non corrette");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Accedi alla tua dashboard" subtitle="Property Advisor ValutaGenio">
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Email">
          <input type="email" className="vg-input" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
        </Field>
        <Field label="Password">
          <input type="password" className="vg-input" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
        </Field>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="login-remember" />
          Ricordami su questo dispositivo
        </label>
        <button className="vg-btn-primary w-full" disabled={loading} data-testid="login-submit">
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Accedi"}
        </button>
      </form>
      <div className="flex justify-between text-sm mt-6 text-gray-500">
        <Link to="/forgot-password" className="hover:text-[#B40101]" data-testid="forgot-link">Password dimenticata?</Link>
        <Link to="/signup" className="hover:text-[#B40101]" data-testid="goto-signup">Registrati</Link>
      </div>
    </AuthShell>
  );
}

export function SignupPage() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState("annuale"); // default piano più conveniente
  const [form, setForm] = useState({
    nome: "", cognome: "", email: "", telefono: "",
    nome_agenzia: "", password: "", password2: "",
    promo_code: "", referred_by: params.get("ref") || "",
    // M8 · PA structured bio
    anni_esperienza: "",
    specializzazioni: [],
    zona_competenza: "",
    descrizione_libera: "",
    // M8 · Code of conduct
    codice_condotta_accepted: false,
  });
  const [promoCheck, setPromoCheck] = useState(null);
  const [validating, setValidating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signupResult, setSignupResult] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const checkPromo = async (code) => {
    if (!code) { setPromoCheck(null); return; }
    setValidating(true);
    try {
      const r = await api.post("/auth/validate-promo", { codice: code });
      setPromoCheck(r.data);
    } catch {
      setPromoCheck({ valid: false, message: "Errore validazione" });
    } finally {
      setValidating(false);
    }
  };

  const canStep1 = form.nome && form.cognome && form.email && form.telefono
    && form.password.length >= 6 && form.password === form.password2
    // M8 · PA bio + COC gate
    && Number.isFinite(parseInt(form.anni_esperienza, 10))
    && parseInt(form.anni_esperienza, 10) >= 0
    && form.specializzazioni.length >= 1
    && form.zona_competenza.trim().length >= 2
    && form.codice_condotta_accepted === true;

  const doSignup = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        plan: selectedPlan,
        anni_esperienza: parseInt(form.anni_esperienza, 10),
      };
      delete payload.password2;
      if (!payload.promo_code) delete payload.promo_code;
      const r = await signup(payload);
      setSignupResult(r);
      if (r.requires_payment) {
        setStep(2);
        // Move on to Stripe
        setRedirecting(true);
        const cr = await api.post("/billing/checkout", { origin_url: window.location.origin, plan: selectedPlan, product: "pa_subscription" });
        if (cr.data.url) {
          window.location.href = cr.data.url;
          return;
        }
        setRedirecting(false);
      } else {
        setStep(3);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore registrazione");
    } finally {
      setLoading(false);
    }
  };

  const planMeta = PLANS.find((p) => p.id === selectedPlan) || PLANS[0];

  if (step === 3 && signupResult) {
    return <SignupSuccess user={signupResult.user} onContinue={() => nav("/agent")} />;
  }

  if (step === 2) {
    return (
      <AuthShell title="Pagamento abbonamento" subtitle="Verrai reindirizzato a Stripe">
        <div className="text-center py-8">
          {redirecting ? (
            <>
              <Loader2 className="animate-spin mx-auto mb-4 text-[#B40101]" size={32} />
              <p className="text-gray-600">Reindirizzamento a Stripe…</p>
            </>
          ) : (
            <p className="text-gray-600">In attesa di Stripe…</p>
          )}
        </div>
      </AuthShell>
    );
  }

  return (
    <SignupShell>
      <div className="max-w-3xl mx-auto space-y-14 mb-14 mt-6">
        <PerchePASection />
        <ComeFunzionaSection />
      </div>
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6">
          <p className="text-xs uppercase tracking-[0.14em] text-gray-400">
            Passo 3 · Iscriviti
          </p>
        </div>
        <div className="vg-card p-8">
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Diventa Property Advisor</h1>
          <p className="text-sm text-gray-500 mt-1 mb-6">
            Un passo piccolo oggi, una rete di fiducia che cresce da domani.
          </p>
          <ReferralProgramBanner refCode={form.referred_by} />
          <PlanPicker selected={selectedPlan} onSelect={setSelectedPlan} />
          <div className="space-y-4 pt-2">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nome">
            <input className="vg-input" value={form.nome} onChange={(e) => upd("nome", e.target.value)} data-testid="signup-nome" />
          </Field>
          <Field label="Cognome">
            <input className="vg-input" value={form.cognome} onChange={(e) => upd("cognome", e.target.value)} data-testid="signup-cognome" />
          </Field>
        </div>
        <Field label="Email">
          <input className="vg-input" type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="signup-email" />
        </Field>
        <Field label="Telefono">
          <input className="vg-input" value={form.telefono} onChange={(e) => upd("telefono", e.target.value)} data-testid="signup-telefono" />
        </Field>
        <Field label="Nome agenzia (opzionale)">
          <input className="vg-input" value={form.nome_agenzia} onChange={(e) => upd("nome_agenzia", e.target.value)} data-testid="signup-agenzia" />
        </Field>

        {/* M8 · PA structured bio ---------------------------------------- */}
        <div className="pt-4 mt-2 border-t border-gray-100">
          <p className="text-sm font-semibold text-[#0A0A0A] mb-1">Il tuo profilo professionale</p>
          <p className="text-xs text-gray-500 mb-4">
            Queste informazioni servono al matching con i proprietari e sono
            visibili al tuo lead solo dopo la sua richiesta.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Anni di esperienza">
              <input className="vg-input" type="number" min="0" max="70"
                     value={form.anni_esperienza}
                     onChange={(e) => upd("anni_esperienza", e.target.value)}
                     data-testid="signup-anni-esperienza" />
            </Field>
            <Field label="Zona di competenza">
              <input className="vg-input"
                     placeholder="es. Roma centro storico"
                     value={form.zona_competenza}
                     onChange={(e) => upd("zona_competenza", e.target.value)}
                     data-testid="signup-zona-competenza" />
            </Field>
          </div>
          <Field label="Specializzazioni (seleziona almeno una)">
            <SpecializzazioniPicker
              value={form.specializzazioni}
              onChange={(v) => upd("specializzazioni", v)}
            />
          </Field>
          <Field label="Descrizione libera (opzionale, max 1000 caratteri)">
            <textarea
              className="vg-input min-h-[80px]" maxLength={1000}
              placeholder="Racconta brevemente il tuo approccio, esperienza, unicità…"
              value={form.descrizione_libera}
              onChange={(e) => upd("descrizione_libera", e.target.value)}
              data-testid="signup-descrizione-libera"
            />
            <span className="text-[10px] text-gray-400">
              {form.descrizione_libera.length}/1000
            </span>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Password">
            <input className="vg-input" type="password" value={form.password} onChange={(e) => upd("password", e.target.value)} data-testid="signup-password" />
          </Field>
          <Field label="Conferma password">
            <input className="vg-input" type="password" value={form.password2} onChange={(e) => upd("password2", e.target.value)} data-testid="signup-password2" />
          </Field>
        </div>
        <Field label="Codice promo (opzionale)">
          <input
            className="vg-input"
            value={form.promo_code}
            onChange={(e) => { upd("promo_code", e.target.value); }}
            onBlur={(e) => checkPromo(e.target.value.trim())}
            data-testid="signup-promo"
            placeholder="Hai un codice? Inseriscilo qui"
          />
          {validating && <p className="text-xs text-gray-500 mt-1">Verifica codice…</p>}
          {promoCheck && (
            <p className={`text-xs mt-2 font-semibold ${promoCheck.valid ? "text-[#10B981]" : "text-[#EF4444]"}`} data-testid="promo-feedback">
              {promoCheck.valid ? `✓ ${promoCheck.message}${selectedPlan === "mensile" ? ` — Prezzo finale: €${promoCheck.final_price.toFixed(2)}/mese` : ""}` : promoCheck.message}
            </p>
          )}
        </Field>

        <div className="rounded-[12px] p-4 mt-2" style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}>
          <p className="text-sm text-gray-500">Costo abbonamento</p>
          <p className="text-2xl font-bold text-[#0A0A0A]" data-testid="signup-final-price">
            {planMeta.price}<span className="text-sm font-normal text-gray-500">{planMeta.period}</span>
          </p>
          {planMeta.equiv && <p className="text-xs text-gray-500 mt-0.5">{planMeta.equiv}</p>}
          {promoCheck?.valid && selectedPlan === "mensile" && (
            <p className="text-xs text-[#10B981] mt-1">Con codice: €{promoCheck.final_price.toFixed(2)}/mese</p>
          )}
          {promoCheck?.valid && promoCheck.sconto_percentuale === 100 && (
            <p className="text-xs text-[#10B981] mt-1">Account FPA - Nessun pagamento richiesto</p>
          )}
        </div>

        {/* M8 · Code of conduct gate — must be explicitly ticked ------- */}
        <label className="flex gap-3 items-start pt-2"
               data-testid="signup-coc-wrapper">
          <input type="checkbox" className="mt-1"
                 checked={form.codice_condotta_accepted}
                 onChange={(e) => upd("codice_condotta_accepted", e.target.checked)}
                 data-testid="signup-coc-checkbox" />
          <span className="text-sm text-gray-600">
            Ho letto e accetto il{" "}
            <a href="/codice-di-condotta" target="_blank" rel="noopener noreferrer"
               className="text-[#B40101] font-semibold underline"
               data-testid="signup-coc-link">
              codice di condotta di ValutaGenio
            </a>.
          </span>
        </label>

        <button className="vg-btn-primary w-full" disabled={!canStep1 || loading} onClick={doSignup} data-testid="signup-submit">
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Continua"}
        </button>

        <p className="text-sm text-center text-gray-500 mt-4">
          Hai già un account? <Link to="/login" className="text-[#B40101] font-semibold">Accedi</Link>
        </p>

        <div className="text-center mt-6 pt-4 border-t border-gray-100">
          <Link
            to="/codice-di-condotta"
            className="text-base font-medium hover:underline"
            style={{ color: "#B40101" }}
            data-testid="signup-howitworks-link"
          >
            Rileggi come funziona ValutaGenio
          </Link>
        </div>
      </div>
      </div>
    </div>
    </SignupShell>
  );
}

function SignupSuccess({ user, onContinue }) {
  const link = `${window.location.origin}/?ref=${user.referral_code}`;
  return (
    <AuthShell title="Benvenuto in ValutaGenio!" subtitle={`Account ${user.ruolo === "fpa" ? "FPA" : "Standard"} attivato`}>
      <div className="text-center space-y-4">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#10B981] flex items-center justify-center">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
        </div>
        <p className="text-gray-600">Ecco il tuo link referral personale:</p>
        <div className="flex gap-2">
          <input className="vg-input text-xs" readOnly value={link} data-testid="signup-success-link" />
          <button
            className="vg-btn-secondary px-4"
            onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copiato!"); }}
            data-testid="signup-copy-link"
          >Copia</button>
        </div>
        <button className="vg-btn-primary w-full" onClick={onContinue} data-testid="signup-go-dashboard">Vai alla dashboard</button>
      </div>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } finally { setLoading(false); }
  };
  return (
    <AuthShell title="Reimposta password" subtitle="Inserisci la tua email">
      {sent ? (
        <div className="text-center py-4">
          <p className="text-[#10B981] font-semibold">Se l'email è registrata, riceverai un link entro pochi minuti.</p>
          <Link to="/login" className="text-sm text-[#B40101] mt-4 inline-block">← Torna al login</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Email">
            <input className="vg-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
          </Field>
          <button className="vg-btn-primary w-full" disabled={loading} data-testid="forgot-submit">
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Invia link reset"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Token mancante o non valido. Richiedi un nuovo link."); return; }
    if (pwd.length < 6) { toast.error("La password deve essere lunga almeno 6 caratteri"); return; }
    if (pwd !== pwd2) { toast.error("Le due password non coincidono"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success("Password aggiornata. Puoi ora accedere con la tua nuova password.");
      nav("/login");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore. Il link potrebbe essere scaduto.");
    } finally { setLoading(false); }
  };
  return (
    <AuthShell title="Nuova password" subtitle="Imposta una nuova password sicura">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Nuova password">
          <input className="vg-input" type="password" required minLength={6} value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="reset-password" />
        </Field>
        <Field label="Conferma nuova password">
          <input className="vg-input" type="password" required minLength={6} value={pwd2} onChange={(e) => setPwd2(e.target.value)} data-testid="reset-password-confirm" />
        </Field>
        <button className="vg-btn-primary w-full" disabled={loading} data-testid="reset-submit">
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Reimposta password"}
        </button>
      </form>
    </AuthShell>
  );
}

export function SignupSuccessReturn() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sid = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const { refresh } = useAuth();

  React.useEffect(() => {
    if (!sid) { setStatus("error"); return; }
    let count = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/billing/status/${sid}`);
        if (r.data.status === "paid") {
          await refresh();
          setStatus("paid");
          setTimeout(() => nav("/agent"), 1500);
          return;
        }
        if (r.data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch {}
      count++;
      if (count < 8) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sid, nav, refresh]);

  return (
    <AuthShell title="Verifica pagamento" subtitle="Stiamo confermando la tua iscrizione">
      <div className="text-center py-6">
        {status === "checking" && <Loader2 className="animate-spin mx-auto text-[#B40101]" size={32} />}
        {status === "paid" && <p className="text-[#10B981] font-semibold">Pagamento confermato! Reindirizzamento…</p>}
        {status === "expired" && <p className="text-[#EF4444]">Sessione scaduta. <Link to="/signup">Riprova</Link></p>}
        {status === "error" && <p className="text-gray-500">Nessuna sessione trovata</p>}
        {status === "timeout" && <p className="text-gray-500">Verifica in corso. Controlla tra qualche istante.</p>}
      </div>
    </AuthShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[#0A0A0A] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="vg-funnel-bg flex items-center justify-center p-4 min-h-screen">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/"><Logo size={28} /></Link>
        </div>
        <div className="vg-card p-8">
          <h1 className="text-2xl font-bold text-[#0A0A0A]">{title}</h1>
          {subtitle && <p className="text-sm text-gray-500 mt-1 mb-6">{subtitle}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}


// M8 · signup layout wider than AuthShell — the PA landing/signup mixes
// long-form marketing intro (perché + come funziona) with the compact
// signup form. Keeping AuthShell untouched avoids collateral changes on
// login/forgot/reset flows.
function SignupShell({ children }) {
  return (
    <div className="vg-funnel-bg min-h-screen py-10 px-4">
      <div className="max-w-3xl mx-auto text-center mb-10">
        <Link to="/"><Logo size={28} /></Link>
      </div>
      {children}
    </div>
  );
}

const PLANS = [
  {
    id: "mensile",
    title: "Mensile",
    price: "79€",
    period: "/mese",
    equiv: null,
    badge: { text: "Più flessibile", tone: "gray" },
    savingsLabel: null,
    extra: "Nessun vincolo di durata",
  },
  {
    id: "semestrale",
    title: "Semestrale",
    price: "395€",
    period: " ogni 6 mesi",
    equiv: "Equivale a 65€/mese",
    badge: { text: "Paghi 5 mesi, il sesto è in omaggio", tone: "gray" },
    savingsLabel: "Risparmi 79€",
    extra: null,
  },
  {
    id: "annuale",
    title: "Annuale",
    price: "711€",
    period: "/anno",
    equiv: "Equivale a 59€/mese",
    badge: { text: "Più conveniente", tone: "red" },
    subBadge: "Paghi 9 mesi, tre te li regaliamo noi",
    savingsLabel: "Risparmi 237€",
    extra: null,
  },
];

function PlanPicker({ selected, onSelect }) {
  return (
    <div className="mb-5" data-testid="plan-picker">
      <p className="text-sm font-semibold text-[#0A0A0A] mb-3">Scegli il tuo piano</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {PLANS.map((p) => {
          const isActive = selected === p.id;
          const badgeStyle = p.badge.tone === "red"
            ? { background: "#B40101", color: "#FFFFFF" }
            : { background: "#F5F5F5", color: "#404040" };
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(p.id)}
              data-testid={`plan-card-${p.id}`}
              className={`text-left rounded-[14px] p-4 transition-all relative ${
                isActive ? "border-2 border-[#B40101] shadow-md" : "border-2 border-gray-200 hover:border-gray-300"
              } bg-white`}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-bold text-[#0A0A0A]">{p.title}</h4>
                {isActive && <span className="text-xs font-bold text-[#B40101]">✓</span>}
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-2xl font-bold text-[#0A0A0A]">{p.price}</span>
                <span className="text-xs text-gray-500">{p.period}</span>
              </div>
              {p.equiv && <p className="text-xs text-gray-500 mb-2">{p.equiv}</p>}
              <span className="inline-block text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full mb-2" style={badgeStyle}>
                {p.badge.text}
              </span>
              {p.subBadge && (
                <p className="text-xs text-[#0A0A0A] font-medium mb-1">{p.subBadge}</p>
              )}
              {p.extra && <p className="text-xs text-gray-500">{p.extra}</p>}
              {p.savingsLabel && (
                <p className="text-xs font-bold mt-2" style={{ color: "#16A34A" }}>{p.savingsLabel}</p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ReferralProgramBanner({ refCode }) {
  return (
    <div className="mb-6 rounded-xl border border-[#B40101]/20 bg-[#FFF6F6] p-5" data-testid="referral-program-banner">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#B40101] mb-2">
        {refCode ? `Sei stato invitato · codice ${refCode}` : "Programma referral 3 generazioni"}
      </p>
      <h3 className="text-lg font-bold text-[#0A0A0A] mb-2">Costruisci una rete di fiducia, guadagni fino a tre generazioni</h3>
      <p className="text-sm text-gray-700 mb-3">
        Con l'abbonamento Property Advisor accedi al motore VG, alla dashboard PA e ai casi strutturati. Se inviti altri professionisti, entrano nella tua rete e generano commissioni sui loro abbonamenti effettivi.
      </p>
      <ul className="text-sm text-gray-700 space-y-1 mb-3">
        <li><strong className="text-[#0A0A0A]">Bronzo</strong> · 1–25 diretti attivi · <strong>30%</strong> sulla 1ª generazione</li>
        <li><strong className="text-[#0A0A0A]">Oro</strong> · 26–50 diretti · <strong>50% + 5%</strong> sulla 2ª</li>
        <li><strong className="text-[#0A0A0A]">Diamante</strong> · 51+ diretti · <strong>70% + 5% + 3%</strong> sulla 3ª</li>
      </ul>
      <p className="text-xs text-gray-500 leading-relaxed">
        Direttamente attivo = professionista invitato da te, ancora abbonato, con maturità minima 60 giorni. Le percentuali si applicano al netto commissionabile di ciascun pagamento effettivo (importo pagato meno IVA applicabile e costi Stripe pertinenti). Il netto non è un valore fisso e dipende dall&apos;IVA del Paese dell&apos;abbonato e dal costo Stripe del singolo pagamento. Esempio illustrativo: un pagamento di 79&nbsp;€ lordi con IVA 22% e costo Stripe 2,34&nbsp;€ produce una base netta ≈ 62,42&nbsp;€; Bronzo G1 riceve ≈ 18,73&nbsp;€. Nessuna promessa di reddito.
      </p>
    </div>
  );
}


// M8 · Specializzazioni multi-select. Fisso il set canonico qui: il backend
// non fa enum validation (accetta stringhe libere) per lasciare margine di
// evoluzione, ma il frontend limita le scelte alle canoniche + un opzione
// "Altro" free-text per catturare specializzazioni non previste.
const SPECIALIZZAZIONI_CANONICHE = [
  "Vendita",
  "Locazione",
  "Locazione breve",
  "Frazionamento",
  "Successione ed eredità",
  "Ristrutturazione",
  "Investimento",
  "Casa vacanza",
  "Studenti",
  "Nuove costruzioni",
];

function SpecializzazioniPicker({ value, onChange }) {
  const [otherInput, setOtherInput] = React.useState("");
  const toggle = (s) => {
    const isOn = value.includes(s);
    onChange(isOn ? value.filter((v) => v !== s) : [...value, s]);
  };
  const addOther = () => {
    const s = otherInput.trim();
    if (!s || value.includes(s)) { setOtherInput(""); return; }
    onChange([...value, s]);
    setOtherInput("");
  };
  return (
    <div data-testid="signup-specializzazioni">
      <div className="flex flex-wrap gap-2">
        {SPECIALIZZAZIONI_CANONICHE.map((s) => {
          const on = value.includes(s);
          return (
            <button
              type="button"
              key={s}
              onClick={() => toggle(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                on
                  ? "bg-[#B40101] text-white border-[#B40101]"
                  : "bg-white text-[#0A0A0A] border-gray-300 hover:border-[#B40101]/40"
              }`}
              data-testid={`signup-spec-${s.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {s}
            </button>
          );
        })}
        {value
          .filter((v) => !SPECIALIZZAZIONI_CANONICHE.includes(v))
          .map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => toggle(s)}
              className="px-3 py-1.5 rounded-full text-xs font-medium
                         bg-[#B40101] text-white border border-[#B40101]"
              data-testid={`signup-spec-custom-${s.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {s} ✕
            </button>
          ))}
      </div>
      <div className="flex gap-2 mt-2">
        <input
          className="vg-input flex-1 text-xs"
          placeholder="Aggiungi altra specializzazione…"
          value={otherInput}
          onChange={(e) => setOtherInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOther(); } }}
          data-testid="signup-spec-other-input"
        />
        <button type="button" onClick={addOther}
                className="vg-btn-secondary text-xs px-3"
                data-testid="signup-spec-other-add">
          + Aggiungi
        </button>
      </div>
    </div>
  );
}



